import type { GameFlow } from "./game-flow";
import type { ProgressState } from "./progress-state";
import type { SeasonalChallengeIdentity } from "../config/challenge-link-config";
import {
  resolveSeasonalChallenge,
  type SeasonalChallengeDefinition,
  type SeasonalChallengeDescriptor,
  type SeasonalChallengeRegistry,
  type SeasonalChallengeStatus,
} from "../config/seasonal-challenge-config";

export interface SeasonalChallengeProgressPort {
  getState(): Pick<ProgressState, "dailyChallengeRecords">;
}

export interface SeasonalChallengeSeedPort {
  useChallenge(seed: string): void;
  useNormal(): void;
}

export interface SeasonalChallengeHomeSnapshot {
  readonly bestLevel: number;
  readonly challenge: SeasonalChallengeDefinition | null;
  readonly completed: boolean;
  readonly descriptor: SeasonalChallengeDescriptor | null;
  readonly status: SeasonalChallengeStatus;
}

export class SeasonalChallengeRuntime {
  private activeChallenge: SeasonalChallengeDefinition | null = null;

  public constructor(
    private readonly flow: GameFlow,
    private readonly progress: SeasonalChallengeProgressPort,
    private readonly seeds: SeasonalChallengeSeedPort,
    private readonly registry: SeasonalChallengeRegistry,
    private readonly now: () => number,
  ) {
    flow.subscribe((snapshot) => {
      if (snapshot.scene === "home" && this.activeChallenge !== null) {
        this.activeChallenge = null;
        this.seeds.useNormal();
      }
    });
  }

  public getActiveChallenge(): SeasonalChallengeDefinition | null {
    return this.activeChallenge;
  }

  public getArenaDescriptor(): SeasonalChallengeDescriptor | null {
    return this.activeChallenge?.descriptor ?? null;
  }

  public getHomeSnapshot(): SeasonalChallengeHomeSnapshot {
    const resolution = resolveSeasonalChallenge(this.registry, this.now());
    const identity = resolution.challenge?.identity;
    const record =
      identity === undefined
        ? undefined
        : this.progress
            .getState()
            .dailyChallengeRecords.find(
              (candidate) => candidate.identity === identity,
            );
    return {
      bestLevel: record?.bestLevel ?? 0,
      challenge: resolution.challenge,
      completed: record?.completed ?? false,
      descriptor: resolution.descriptor,
      status: resolution.status,
    };
  }

  public replay(identity: SeasonalChallengeIdentity): boolean {
    if (this.flow.getSnapshot().scene !== "results") {
      return false;
    }
    const active = this.activeChallenge;
    if (active?.identity !== identity) {
      return false;
    }
    const resolution = resolveSeasonalChallenge(
      this.registry,
      this.now(),
      identity,
    );
    if (resolution.challenge === null) {
      return false;
    }
    return this.startChallenge(resolution.challenge);
  }

  public startCurrent(): boolean {
    if (this.flow.getSnapshot().scene !== "home") {
      return false;
    }
    const resolution = resolveSeasonalChallenge(this.registry, this.now());
    return resolution.challenge === null
      ? false
      : this.startChallenge(resolution.challenge);
  }

  public startIdentity(identity: SeasonalChallengeIdentity): boolean {
    if (this.flow.getSnapshot().scene !== "home") {
      return false;
    }
    const resolution = resolveSeasonalChallenge(
      this.registry,
      this.now(),
      identity,
    );
    if (resolution.challenge === null) {
      this.seeds.useNormal();
      return false;
    }
    return this.startChallenge(resolution.challenge);
  }

  private startChallenge(challenge: SeasonalChallengeDefinition): true {
    this.activeChallenge = challenge;
    this.seeds.useChallenge(challenge.seed);
    this.flow.startMode(challenge.mode);
    return true;
  }
}
